import { memo, useMemo } from "react"
import type { ReactElement } from "react"
import { alpha, Box, Typography, useTheme } from "@mui/material"
import { motion, useReducedMotion } from "framer-motion"
import { PartyIcon } from "@/components/icons"
import { PRICING_REWARD_SUMMARY_ENTER_S } from "@/components/pricing/pricing-tokens"
import { createCurrencyFormatter } from "@/utils/locale-format"
import { designTokens } from "@/theme"

const MONEY_EPS = 0.005

export type RewardSummaryProps = {
  savings: number
  nextSavings?: {
    requiredAmount: number
    potentialSavings: number
  } | null
  /**
   * Optional backend SavingsSummary breakdown (display-only — never computed here).
   */
  savingsBreakdown?: {
    menuOfferSavings?: number
    couponSavings?: number
  } | null
  /** ISO 4217 code — used when `formatMoney` is omitted. */
  currency: string
  formatMoney?: (amount: number) => string
  /**
   * Legacy prop — premium layout no longer uses emoji.
   * Kept for call-site compatibility; does not change savings amount or logic.
   */
  celebration?: boolean
}

function defaultFormatMoney(currency: string): (amount: number) => string {
  return createCurrencyFormatter(currency)
}

function RewardSummaryInner({
  savings,
  nextSavings,
  savingsBreakdown,
  currency,
  formatMoney: formatMoneyProp,
}: RewardSummaryProps): ReactElement | null {
  const theme = useTheme()
  const reduceMotion = useReducedMotion()
  const formatMoney = useMemo(
    () => formatMoneyProp ?? defaultFormatMoney(currency),
    [formatMoneyProp, currency]
  )

  const showSavings = savings > MONEY_EPS
  let nextRequired = 0
  let nextPotential = 0
  if (nextSavings != null) {
    nextRequired = nextSavings.requiredAmount
    nextPotential = nextSavings.potentialSavings
  }
  const showNext = nextRequired > MONEY_EPS && nextPotential > MONEY_EPS
  const menuOffers = savingsBreakdown?.menuOfferSavings ?? 0
  const couponSavings = savingsBreakdown?.couponSavings ?? 0
  const showMenuOffers = menuOffers > MONEY_EPS
  const showCoupon = couponSavings > MONEY_EPS

  if (!showSavings && !showNext) {
    return null
  }

  const nextLine = showNext
    ? `You're ${formatMoney(nextRequired)} away from saving ${formatMoney(nextPotential)} more`
    : null

  const ariaLabel = showSavings && showNext ? "Savings and next reward" : showSavings ? "Savings" : "Next reward"

  const isLight = theme.palette.mode === "light"
  /** Warm amber / gold — brand `warning` token (delight, not success). */
  const celebrationAccent = theme.palette.warning.main
  /** Soft light disk — lighter than promo ivory for clearer amber contrast. */
  const celebrationIconBg = isLight
    ? theme.palette.common.white
    : alpha(theme.palette.common.white, 0.16)

  const inner = (
    <Box
      role="region"
      aria-label={ariaLabel}
      sx={{
        borderRadius: `${designTokens.shape.borderRadius.lg}px`,
        px: 1.5,
        py: 1.5,
        mb: 1.5,
        backgroundColor: alpha(theme.palette.success.main, isLight ? 0.06 : 0.12),
        border: `1px solid ${alpha(theme.palette.success.main, isLight ? 0.12 : 0.22)}`,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.25),
      }}
    >
      {showSavings ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(0.75),
            minWidth: 0,
          }}
        >
          {/* Celebration row — warm amber delight, not success */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(1),
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: theme.spacing(4.5),
                height: theme.spacing(4.5),
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: celebrationIconBg,
                color: celebrationAccent,
                border: "none",
              }}
              aria-hidden
            >
              <PartyIcon width={18} height={18} />
            </Box>
            <Typography
              component="p"
              sx={{
                m: 0,
                fontSize: theme.typography.pxToRem(13),
                fontWeight: theme.typography.fontWeightBold,
                color: celebrationAccent,
                lineHeight: 1.35,
                letterSpacing: "0.01em",
              }}
            >
              Nice choice
            </Typography>
          </Box>

          {/* Hero savings amount — strongest visual (success green) */}
          <Typography
            component="p"
            sx={{
              m: 0,
              fontSize: theme.typography.pxToRem(22),
              fontWeight: theme.typography.fontWeightBold,
              color: theme.palette.success.main,
              lineHeight: 1.25,
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
              wordBreak: "break-word",
            }}
          >
            You saved {formatMoney(savings)} 
          </Typography>

          {/* Supporting explanation — muted, never competes */}
          <Typography
            component="p"
            sx={{
              m: 0,
              mt: 0.25,
              fontSize: theme.typography.pxToRem(12),
              fontWeight: theme.typography.fontWeightRegular,
              color: theme.palette.text.secondary,
              lineHeight: 1.45,
            }}
          >
            Includes menu offers and your applied discount.
          </Typography>
          {showMenuOffers || showCoupon ? (
            <Box
              component="ul"
              sx={{
                m: 0,
                mt: 0.75,
                pl: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              {showMenuOffers ? (
                <Typography component="li" variant="caption" color="text.secondary">
                  Menu offers {formatMoney(menuOffers)}
                </Typography>
              ) : null}
              {showCoupon ? (
                <Typography component="li" variant="caption" color="text.secondary">
                  Coupon {formatMoney(couponSavings)}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}
      {nextLine ? (
        <Typography
          component="p"
          sx={{
            m: 0,
            fontSize: theme.typography.pxToRem(13),
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.palette.text.secondary,
            lineHeight: 1.4,
          }}
        >
          {nextLine}
        </Typography>
      ) : null}
    </Box>
  )

  if (reduceMotion) {
    return inner
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: PRICING_REWARD_SUMMARY_ENTER_S, ease: [0.33, 1, 0.68, 1] }}
      style={{ willChange: "opacity, transform" }}
    >
      {inner}
    </motion.div>
  )
}

export const RewardSummary = memo(RewardSummaryInner)
